import {downloadExcelProgram, validateExcelProgram} from "@/iamteacherpc/service/excelProgramService.js";
import ACCEPTED_DOMAIN from "@common/consts/accepted-domain";
import {postMessageToParentWindowPromise} from "@common/utils/postToParentWindow";

let resize = false;

function _iframeResize(domain) {
    const height = document.body.clientHeight;
    window.parent.postMessage(
        {
            type: 'IFRAME_RESIZE',
            data: {
                height,
            },
        },
        domain,
    );
}

function iframeResize(domain) {
    if (!resize) {
        return
    }
    _iframeResize(domain);
    window.requestAnimationFrame(() => {
        iframeResize(domain);
    });
}

function getCurrentScroll(win) {
    const doc = win.document.documentElement;

    return {
        left: (win.pageXOffset || doc.scrollLeft) - (doc.clientLeft || 0),
        top: (win.pageYOffset || doc.scrollTop)  - (doc.clientTop || 0)
    }
}

export async function iframeResizeStart() {
    resize = true;
    // window parent 도메인 불러오기 - 안그러면 재귀호출로인해 부하심해짐
    // 원래 이보다 더 좋은 방법은 상위 window에서 도메인 정보를 먼저 주는 것인데 그렇게되면 작업량이 너무 커지므로
    // 일단 응급처치식으로..
    const results = await Promise.allSettled(
        ACCEPTED_DOMAIN[process.env.VUE_APP_TARGET_ORIGIN].map(async origin => {
            return await postMessageToParentWindowPromise(
                window.parent,
                'GET_DOMAIN',
                {},
                origin
            )
        })
    )
    const fulfilledEl = results.find(res => res.status === 'fulfilled')

    window.requestAnimationFrame(() => {
        iframeResize(fulfilledEl.value.domain)
    });
}

export function iframeResizeEnd() {
    resize = false;
}

export function inIframe() {
    try {
        return window.self !== window.top;
    } catch (e) {
        return false;
    }
}

export function requestScroll(scrollTop) {
    _iframeResize();

    if (inIframe()) {
        try {
            // postMessage 먼저 보낼 때
            ACCEPTED_DOMAIN[process.env.VUE_APP_TARGET_ORIGIN].forEach(origin => {
                window.parent.postMessage(
                    {
                        type: 'SCROLL_TO',
                        data: {
                            scrollTop,
                        }
                    },
                    origin,
                )
            })
        } catch (e) {
            // ...
        }
    } else {
        const scroll = getCurrentScroll(window);
        window.scrollTo(scroll.left, scrollTop);
    }
}

const requestConfirmParentWindow = ({title, titleHtml, desc, descHtml, okText, cancelText}) => new Promise((resolve, reject) => {
    const messageHandler = event => {
        if (event.data.type === 'REQUEST_HANDLER') {
            window.removeEventListener('message', messageHandler);

            if (event.data.data.result === 'resolve') {
                // event 리스너의 순서에 영향을 받지 않기 위해 그냥 다음 콘트롤 플로우 떄 실행한다.
                setTimeout(resolve, 0);
            } else if (event.data.data.result === 'reject') {
                setTimeout(reject, 0);
            }
        }
    }

    window.addEventListener('message', messageHandler);

    try {
        // postMessage 먼저 보낼 때
        ACCEPTED_DOMAIN[process.env.VUE_APP_TARGET_ORIGIN].forEach(origin => {
            window.parent.postMessage(
                {
                    type: 'REQUEST_HANDLER',
                    data: {
                        title,
                        titleHtml: titleHtml || title.replace('\n', '<br>'),
                        desc,
                        descHtml: descHtml || desc && desc.replace('\n', '<br>'),
                        okText,
                        cancelText,
                    },
                },
                origin,
            )
        })
    } catch (e) {
        // ...
    }
})

export function requestConfirm({title, titleHtml, desc, descHtml, okText, cancelText}) {
    if (inIframe()) {
        return requestConfirmParentWindow({title, titleHtml, desc, descHtml, okText, cancelText});
    } else {
        return new Promise(function (resolve, reject) {
            if (confirm(`${title}\n\n${desc || ''}`)) {
                resolve();
            } else {
                reject();
            }
        });
    }
}

const requestAlertParentWindow = ({title, titleHtml, desc, descHtml, okText}) => new Promise((resolve, reject) => {
    const messageHandler = event => {
        if (event.data.type === 'REQUEST_ALERT') {
            window.removeEventListener('message', messageHandler);

            if (event.data.data.result === 'resolve') {
                // event 리스너의 순서에 영향을 받지 않기 위해 그냥 다음 콘트롤 플로우 떄 실행한다.
                setTimeout(resolve, 0);
            } else if (event.data.data.result === 'reject') {
                setTimeout(reject, 0);
            }
        }
    }

    window.addEventListener('message', messageHandler);

    try {
        // postMessage 먼저 보낼 때
        ACCEPTED_DOMAIN[process.env.VUE_APP_TARGET_ORIGIN].forEach(origin => {
            window.parent.postMessage(
                {
                    type: 'REQUEST_ALERT',
                    data: {
                        title,
                        titleHtml: titleHtml || title.replace('\n', '<br>'),
                        desc,
                        descHtml: descHtml || desc && desc.replace('\n', '<br>'),
                        okText,
                    }
                },
                origin,
            )
        })
    } catch (e) {
        // ...
    }
})

export function requestAlert({title, titleHtml, desc, descHtml, okText}) {
    if (inIframe()) {
        return requestAlertParentWindow({title, titleHtml, desc, descHtml, okText});
    } else {
        return new Promise(function (resolve, reject) {
            alert(title);
            resolve();
        });
    }
}

const requestExcelProgramParentWindow = ({enrolmentType, programs, filters, isAllowDuplicateApply}) => new Promise(async (resolve, reject) => {
    const messageHandler = event => {
        switch (event.data.type) {
            case 'REQUEST_EXCEL_PROGRAM':
                window.removeEventListener('message', messageHandler);

                if (event.data.data.result === 'resolve') {
                    // event 리스너의 순서에 영향을 받지 않기 위해 그냥 다음 콘트롤 플로우 떄 실행한다.
                    setTimeout(resolve, 0);
                } else if (event.data.data.result === 'reject') {
                    setTimeout(reject, 0);
                }
                break;
            case 'NEXT_ON_FILE_VALIDATE_START':
                validateExcelProgram({
                    fileBase64: event.data.data.fileBase64,
                    filters: event.data.data.filters,
                    enrolmentType: event.data.data.enrolmentType,
                    isAllowDuplicateApply: event.data.data.isAllowDuplicateApply,
                    targetOrigin: event.origin,
                });
                break;
            case 'ADD_PROGRAM_BULK':
                setTimeout(() => {
                    resolve(event.data.data.programs);
                }, 0);
                window.removeEventListener('message', messageHandler);
                break;
            default:
                break;
        }
    }

    window.addEventListener('message', messageHandler);

    const download = await downloadExcelProgram({enrolmentType, programs, filters});

    try {
        // postMessage 먼저 보낼 때
        ACCEPTED_DOMAIN[process.env.VUE_APP_TARGET_ORIGIN].forEach(origin => {
            window.parent.postMessage(
                {
                    type: 'REQUEST_EXCEL_PROGRAM',
                    data: {
                        download,
                        isAllowDuplicateApply,
                        filters,
                        enrolmentType,
                        programs,
                    },
                },
                origin,
            )
        })
    } catch (e) {
        // ...
    }
})

export function requestExcelProgram({enrolmentType, programs, filters, isAllowDuplicateApply}) {
    if (!inIframe()) {
        return Promise.resolve();
    }

    return requestExcelProgramParentWindow({
        enrolmentType,
        programs,
        filters,
        isAllowDuplicateApply
    });
}

const requestDrawPriorBulkParentWindow = ({programs}) => new Promise((resolve, reject) => {
    const messageHandler = event => {
        switch (event.data.type) {
            case 'REQUEST_DRAW_PRIOR_BULK':
                window.removeEventListener('message', messageHandler);

                if (event.data.data.result === 'resolve') {
                    // event 리스너의 순서에 영향을 받지 않기 위해 그냥 다음 콘트롤 플로우 떄 실행한다.
                    setTimeout(resolve, 0);
                } else if (event.data.data.result === 'reject') {
                    setTimeout(reject, 0);
                }
                break;
            case 'SET_DRAW_PRIORS_BULK':
                setTimeout(() => {
                    resolve(event.data.data.drawPriors);
                    window.parent.postMessage(
                        {
                            type: 'SET_DRAW_PRIORS_BULK_END',
                        },
                        event.origin,
                    )
                    window.removeEventListener('message', messageHandler);
                }, 0);
                break;
            default:
                break;
        }
    }

    window.addEventListener('message', messageHandler);

    try {
        // postMessage 먼저 보낼 때
        ACCEPTED_DOMAIN[process.env.VUE_APP_TARGET_ORIGIN].forEach(origin => {
            window.parent.postMessage(
                {
                    type: 'REQUEST_DRAW_PRIOR_BULK',
                    data: {
                        programs,
                    }
                },
                origin,
            )
        })
    } catch (e) {
        // ...
    }
})

export function requestDrawPriorsBulk({programs}) {
    if (!inIframe()) {
        return Promise.resolve();
    }

    return requestDrawPriorBulkParentWindow({
        programs: JSON.parse(JSON.stringify(programs)),
    });
}

const requestDrawPriorsParentWindow = ({drawPriors}) => new Promise((resolve, reject) => {
    const messageHandler = event => {
        switch (event.data.type) {
            case 'REQUEST_DRAW_PRIORS':
                window.removeEventListener('message', messageHandler);

                if (event.data.data.result === 'resolve') {
                    // event 리스너의 순서에 영향을 받지 않기 위해 그냥 다음 콘트롤 플로우 떄 실행한다.
                    setTimeout(resolve, 0);
                } else if (event.data.data.result === 'reject') {
                    setTimeout(reject, 0);
                }
                break;
            case 'REQUEST_DRAW_PRIORS_SET_DRAW_PRIORS':
                setTimeout(() => {
                    resolve({
                        isCancel: false,
                        drawPriors: event.data.data.drawPriors,
                    });
                    window.removeEventListener('message', messageHandler);
                }, 0);
                break;
            case 'REQUEST_DRAW_PRIORS_CANCEL_CALLBACK':
                setTimeout(() => {
                    resolve({
                        isCancel: true,
                    });
                    window.removeEventListener('message', messageHandler);
                }, 0);
                break;
            default:
                break;
        }
    }

    window.addEventListener('message', messageHandler);

    try {
        // postMessage 먼저 보낼 때
        ACCEPTED_DOMAIN[process.env.VUE_APP_TARGET_ORIGIN].forEach(origin => {
            window.parent.postMessage(
                {
                    type: 'REQUEST_DRAW_PRIORS',
                    data: {
                        drawPriors: JSON.parse(JSON.stringify(drawPriors)),
                    }
                },
                origin,
            )
        })
    } catch (e) {
        // ...
    }
})
// 선추첨 대상자 지정
export function requestDrawPriors({drawPriors}) {
    if (!inIframe()) {
        return Promise.resolve();
    }

    return requestDrawPriorsParentWindow({drawPriors});
}

const requestGaHelperParentWindow = ({view_category, view_area, view_label}) => new Promise((resolve, reject) => {
    const messageHandler = event => {
        switch (event.data.type) {
            case 'GA_HELPER':
                window.removeEventListener('message', messageHandler);

                if (event.data.data.result === 'resolve') {
                    // event 리스너의 순서에 영향을 받지 않기 위해 그냥 다음 콘트롤 플로우 떄 실행한다.
                    setTimeout(resolve, 0);
                } else if (event.data.data.result === 'reject') {
                    setTimeout(reject, 0);
                }
                break;
            default:
                break;
        }
    }

    window.addEventListener('message', messageHandler);

    try {
        // postMessage 먼저 보낼 때
        ACCEPTED_DOMAIN[process.env.VUE_APP_TARGET_ORIGIN].forEach(origin => {
            window.parent.postMessage(
                {
                    type: 'GA_HELPER',
                    data: {
                        view_category,
                        view_area,
                        view_label,
                    }
                },
                origin,
            )
        })
    } catch (e) {
        // ...
    }
})

export function gaHelper({view_category, view_area, view_label}) {
    if (inIframe()) {
        return requestGaHelperParentWindow({view_category, view_area, view_label});
    } else {
        console.error('iframe 으로 불러와지지 않았습니다.');
    }
}
